import borders from 'shared/theme/base/borders';
import boxShadows from 'shared/theme/base/boxShadows';
import breakpoints from 'shared/theme/base/breakpoints';
import colors from 'shared/theme/base/colors';
import globals from 'shared/theme/base/globals';
import typography from 'shared/theme/base/typography';
import appBar from 'shared/theme/components/appBar';
import avatar from 'shared/theme/components/avatar';
import breadcrumbs from 'shared/theme/components/breadcrumbs';
import button from 'shared/theme/components/button/index';
import buttonBase from 'shared/theme/components/buttonBase';
import cardContent from 'shared/theme/components/card/cardContent';
import cardMedia from 'shared/theme/components/card/cardMedia';
import card from 'shared/theme/components/card/index';
import container from 'shared/theme/components/container';
import divider from 'shared/theme/components/divider';
import flatpickr from 'shared/theme/components/flatpickr';
import autocomplete from 'shared/theme/components/form/autocomplete';
import checkbox from 'shared/theme/components/form/checkbox';
import formControlLabel from 'shared/theme/components/form/formControlLabel';
import formLabel from 'shared/theme/components/form/formLabel';
import input from 'shared/theme/components/form/input';
import inputBase from 'shared/theme/components/form/inputBase';
import radio from 'shared/theme/components/form/radio';
import select from 'shared/theme/components/form/select';
import switchButton from 'shared/theme/components/form/switchButton';
import icon from 'shared/theme/components/icon';
import iconButton from 'shared/theme/components/iconButton';
import linearProgress from 'shared/theme/components/linearProgess';
import link from 'shared/theme/components/link';
import list from 'shared/theme/components/list/index';
import listItem from 'shared/theme/components/list/listItem';
import listItemText from 'shared/theme/components/list/listItemText';
import menu from 'shared/theme/components/menu/index';
import menuItem from 'shared/theme/components/menu/menuItem';
import popover from 'shared/theme/components/popover';
// Soft UI Dashboard PRO React components base styles for @mui material components
import sidenav from 'shared/theme/components/sidenav';

import slider from 'shared/theme/components/slider';
import stepper from 'shared/theme/components/stepper/index';
import step from 'shared/theme/components/stepper/step';
import stepConnector from 'shared/theme/components/stepper/stepConnector';
import stepIcon from 'shared/theme/components/stepper/stepIcon';
import stepLabel from 'shared/theme/components/stepper/stepLabel';
import svgIcon from 'shared/theme/components/svgIcon';
import swal from 'shared/theme/components/swal';
import tableCell from 'shared/theme/components/table/tableCell';
import tableContainer from 'shared/theme/components/table/tableContainer';
import tableHead from 'shared/theme/components/table/tableHead';
import tabs from 'shared/theme/components/tabs/index';
import tab from 'shared/theme/components/tabs/tab';
import tooltip from 'shared/theme/components/tooltip';
import boxShadow from 'shared/theme/functions/boxShadow';
import hexToRgb from 'shared/theme/functions/hexToRgb';
import linearGradient from 'shared/theme/functions/linearGradient';
import pxToRem from 'shared/theme/functions/pxToRem';
import rgba from 'shared/theme/functions/rgba';
import { createTheme } from '@mui/material';
import { Borders } from './base/borders';
import { BoxShadows } from './base/boxShadows';
import { Colors } from './base/colors';
import { Typography } from './base/typography';

interface ThemeFunctions {
  boxShadow: typeof boxShadow;
  hexToRgb: typeof hexToRgb;
  linearGradient: typeof linearGradient;
  pxToRem: typeof pxToRem;
  rgba: typeof rgba;
}

declare module '@mui/material/styles/createTheme' {
  export interface Theme {
    palette: Colors;
    boxShadows: BoxShadows;
    borders: Borders;
    functions: ThemeFunctions;
    typography: Typography;
  }
}

declare module '@mui/material/styles' {
  export interface TypeText {
    main: string;
    focus: string;
  }

  export interface ThemeOptions {
    boxShadows: BoxShadows;
    borders: Borders;
    functions: ThemeFunctions;
  }
}

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...swal,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInputBase: { ...inputBase },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiInput: { ...input },
    MuiOutlinedInput: { ...input },
    MuiFilledInput: { ...input },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
  },
});
